import { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Search, Brain, CheckCircle, RefreshCw } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { PageShell } from '../shared/PageShell';
import { runLabAnalyst } from '../../services/agents/labAnalyst';
import { persistCaptainOutput } from '../../services/firestore/analysisService';
import { ProcessingDelayedNotification } from '../shared/ProcessingDelayedNotification';

const MAX_RETRIES = 3;

/** Error messages that indicate model unavailability (retriable via queue) */
const QUEUE_ELIGIBLE_PATTERNS = [
  'temporarily unavailable',
  'rate limit',
  'timeout',
  'overloaded',
  'capacity',
  '529',
  '503',
  '429',
];

const steps = [
  { id: 'upload', icon: Upload, label: 'Receiving your labs', duration: 1200 },
  { id: 'parse', icon: Search, label: 'Extracting biomarkers', duration: 2000 },
  { id: 'analyze', icon: Brain, label: 'Analyzing against longevity benchmarks', duration: 2500 },
  { id: 'complete', icon: CheckCircle, label: 'Analysis complete', duration: 800 },
];

export function ProcessingScreen() {
  const { state, dispatch } = useApp();
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  // CAP.18 D#5: Track when labs are queued for delayed processing
  const [isProcessingDelayed, setIsProcessingDelayed] = useState(false);
  // Keep file reference for retry — don't delete on error
  const fileRef = useRef<File | null>(window.__hemologixUpload ?? null);

  const runAnalysis = useCallback(async (cancelled: { current: boolean }) => {
    setError(null);
    setCurrentStep(0);

    // Animate through steps
    for (let i = 0; i < steps.length - 1; i++) {
      if (cancelled.current) return;
      await new Promise((r) => setTimeout(r, steps[i].duration));
      if (cancelled.current) return;
      setCurrentStep(i + 1);
    }

    // Run the actual Lab Analyst (or mock)
    try {
      const file = fileRef.current;
      const report = await runLabAnalyst(file ?? null, state.mockMode);
      if (cancelled.current) return;

      // CLINICAL SAFETY: Verify we didn't receive mock data in production
      if (report.isMockData) {
        const host = typeof window !== 'undefined' ? window.location.hostname : '';
        if (host === 'hemologix.com' || host === 'www.hemologix.com' ||
            host.endsWith('.web.app') || host.endsWith('.firebaseapp.com')) {
          console.error('[CLINICAL SAFETY] Mock lab report returned on production hostname. Refusing to display.');
          setError('A system error occurred. Your results could not be verified. Please try again or contact support.');
          return;
        }
      }

      dispatch({ type: 'SET_LAB_REPORT', payload: report });
      persistCaptainOutput(report).catch((err) => console.warn('[MCP Persist] Captain write failed:', err));

      // Brief pause on "complete" before navigating
      await new Promise((r) => setTimeout(r, 1000));
      if (cancelled.current) return;

      // Clean up file reference on success
      fileRef.current = null;
      delete window.__hemologixUpload;

      dispatch({ type: 'SET_SCREEN', payload: 'chat' });
    } catch (err) {
      if (cancelled.current) return;
      const errorMsg = err instanceof Error ? err.message : 'Analysis failed. Please try again.';

      // CAP.18 D#5: If the error indicates model unavailability,
      // show "processing delayed" instead of "failed" (c1).
      // Labs are queued server-side for automatic retry.
      const isQueueEligible = QUEUE_ELIGIBLE_PATTERNS.some(
        (pattern) => errorMsg.toLowerCase().includes(pattern),
      );

      if (isQueueEligible) {
        // c3: Show delayed notification immediately after detecting queue-eligible failure
        setIsProcessingDelayed(true);
        setError(null);
      } else {
        setError(errorMsg);
      }
      setIsRetrying(false);
    }
  }, [state.mockMode, dispatch]);

  useEffect(() => {
    const cancelled = { current: false };
    runAnalysis(cancelled);
    return () => { cancelled.current = true; };
  }, [runAnalysis]);

  const handleRetry = () => {
    if (retryCount >= MAX_RETRIES) return;
    setRetryCount((c) => c + 1);
    setIsRetrying(true);
    const cancelled = { current: false };
    runAnalysis(cancelled);
  };

  const handleBackToUpload = () => {
    // Clean up file reference when going back
    fileRef.current = null;
    delete window.__hemologixUpload;
    dispatch({ type: 'SET_SCREEN', payload: 'landing' });
  };

  return (
    <PageShell>
      <div className="flex flex-col items-center justify-center min-h-screen px-6">
        {/* Pulsing glow */}
        <motion.div
          className="absolute w-[300px] h-[300px] rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, var(--accent-light) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />

        <div className="relative z-10 flex flex-col items-center gap-8 max-w-sm w-full">
          {/* Brand */}
          <h2 className="font-display text-xl font-bold">
            HEMOLOGIX<span className="text-gold">.AI</span>
          </h2>

          {/* Steps */}
          <div className="w-full flex flex-col gap-4">
            <AnimatePresence mode="wait">
              {steps.map((step, idx) => {
                const Icon = step.icon;
                const isActive = idx === currentStep;
                const isDone = idx < currentStep;
                const isPending = idx > currentStep;

                return (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{
                      opacity: isPending ? 0.3 : 1,
                      x: 0,
                    }}
                    transition={{ duration: 0.3, delay: idx * 0.1 }}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                      isActive ? 'bg-bg-card' : ''
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isDone
                          ? 'bg-optimal/20 text-optimal'
                          : isActive
                            ? 'bg-accent/20 text-accent-light'
                            : 'bg-bg-tertiary text-text-muted'
                      }`}
                    >
                      {isDone ? (
                        <CheckCircle size={16} />
                      ) : (
                        <Icon size={16} />
                      )}
                    </div>
                    <span
                      className={`text-sm ${
                        isDone
                          ? 'text-text-secondary'
                          : isActive
                            ? 'text-text-primary font-medium'
                            : 'text-text-muted'
                      }`}
                    >
                      {step.label}
                    </span>
                    {isActive && !error && (
                      <motion.div
                        className="ml-auto flex gap-1"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        {[0, 1, 2].map((i) => (
                          <motion.div
                            key={i}
                            className="w-1.5 h-1.5 rounded-full bg-accent-light"
                            animate={{ opacity: [0.3, 1, 0.3] }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              delay: i * 0.2,
                            }}
                          />
                        ))}
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {/* CAP.18 D#5: Processing delayed notification (c1, c2, c3, c4) */}
          {isProcessingDelayed && !error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <ProcessingDelayedNotification
                estimatedMinutes={15}
                onUploadDifferent={handleBackToUpload}
              />
            </motion.div>
          )}

          {/* Error state with retry */}
          {error && !isProcessingDelayed && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full p-4 rounded-lg bg-low/10 border border-low/20 text-center"
            >
              <p className="text-low text-sm">{error}</p>

              <div className="mt-3 flex flex-col items-center gap-2">
                {retryCount < MAX_RETRIES && fileRef.current && (
                  <button
                    onClick={handleRetry}
                    disabled={isRetrying}
                    className="flex items-center gap-1.5 text-sm font-medium text-accent-light hover:text-accent-dark disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw size={14} className={isRetrying ? 'animate-spin' : ''} />
                    {isRetrying ? 'Retrying...' : `Retry analysis (${MAX_RETRIES - retryCount} left)`}
                  </button>
                )}

                {retryCount >= MAX_RETRIES && (
                  <p className="text-xs text-text-muted">
                    We were unable to analyze your labs after {MAX_RETRIES} attempts.
                    Please try uploading again or contact support.
                  </p>
                )}

                <button
                  onClick={handleBackToUpload}
                  className="text-xs text-text-secondary underline hover:text-text-primary"
                >
                  Upload different labs
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </PageShell>
  );
}
